// Lifted verbatim from the widget's state/service-hours.ts (parity Phase
// 0.5); loadServiceHours takes the app's client as its first argument.
// Client-side helpers for operating-hours enforcement.
//
// Server side: migration 20260521000002 added `service_hours` rows
// (per service, per day-of-week, opens_at/closes_at time, is_closed),
// plus check_service_hours() called by a BEFORE INSERT trigger on
// public.bookings. The trigger raises `outside_operating_hours` if a
// hold request falls outside the configured windows.
//
// Client side (this file): we read the same data via the
// `list_service_hours` RPC and use it to:
//   (a) constrain the datetime-local input's min/max so customers
//       physically can't pick a time outside hours;
//   (b) render an "Open today 12:00–22:00" pill near the WHEN row;
//   (c) surface `outside_operating_hours` rejections gracefully if
//       they still slip through (e.g. server hours changed between
//       page load and submit).
//
// Hours are looked up by the customer's local clock — the server uses
// the tenant timezone, the widget uses the customer's browser tz.
// That's a known minor mismatch: a tenant in JNB and a customer in NY
// see different "today" windows. The widget falls back gracefully —
// if our min/max clamps a time the server would actually accept, the
// customer just re-picks. The server is the source of truth.

import type { BookingClient } from "./client";

export interface ServiceHoursRow {
  id: string;
  day_of_week: number; // 0 = Sunday … 6 = Saturday
  is_closed: boolean;
  opens_at: string | null; // "HH:MM:SS"
  closes_at: string | null;
  label: string | null;
  display_order: number;
}

/**
 * Load operating hours for a service. Returns `[]` when the service
 * has no rows configured (the server-side check is fail-open in that
 * case, so we don't need to constrain the input either).
 */
export async function loadServiceHours(
  client: BookingClient,
  serviceId: string,
): Promise<ServiceHoursRow[]> {
  const { data, error } = await client.rpc("list_service_hours", {
    p_service_id: serviceId,
  });
  if (error || !Array.isArray(data)) {
    return [];
  }
  return data as ServiceHoursRow[];
}

/**
 * Compute the open windows for a given local date. Returns an array
 * because a venue can split lunch + dinner (two rows for the same dow).
 * Crossing-midnight rows (closes_at < opens_at) are returned with the
 * close moved to the next calendar day.
 *
 * Returned windows are in the customer's local timezone (Date objects).
 */
export function windowsForDate(
  hours: readonly ServiceHoursRow[],
  date: Date,
): { start: Date; end: Date; label: string | null }[] {
  const dow = date.getDay();
  const out: { start: Date; end: Date; label: string | null }[] = [];
  for (const row of hours) {
    if (row.day_of_week !== dow) continue;
    if (row.is_closed) continue;
    if (!row.opens_at || !row.closes_at) continue;
    const [oh, om] = parseTime(row.opens_at);
    const [ch, cm] = parseTime(row.closes_at);
    const start = new Date(date);
    start.setHours(oh, om, 0, 0);
    const end = new Date(date);
    end.setHours(ch, cm, 0, 0);
    if (end <= start) {
      // Crosses midnight — push end to the next day.
      end.setDate(end.getDate() + 1);
    }
    out.push({ start, end, label: row.label });
  }
  out.sort((a, b) => a.start.getTime() - b.start.getTime());
  return out;
}

/**
 * Loose lower bound for the WHEN picker — just "not in the past".
 *
 * The previous implementation returned today's open + close as
 * `{ min, max }` on the assumption that the browser would only enforce
 * the time portion. In practice, HTML `<input type="datetime-local">`
 * applies `min`/`max` to the FULL datetime, including the date — so
 * the calendar picker refused to let customers navigate to future
 * dates at all. Reported as "Cannot change date for reservation?"
 *
 * New contract:
 *   - `min` is always the current local time (no past bookings)
 *   - No `max` — the customer can pick any future date / time
 *   - The hours pill above the picker still shows today's open
 *     window so the customer knows the constraint
 *   - The server's BEFORE INSERT trigger on `bookings`
 *     (`trg_enforce_service_hours` from PR #67) rejects bookings
 *     outside hours and CartPanel surfaces the friendly error
 *
 * Returning the `min` always (even when no hours are configured) is
 * a tiny UX win — no past times — and matches the prototype.
 */
export function inputBoundsForDate(
  _hours: readonly ServiceHoursRow[],
  _date: Date,
): { min: string; max?: string } | null {
  return { min: toLocalInputValue(new Date()) };
}

/**
 * Human-readable "Open today 12:00–22:00" / "Closed today" label
 * for the hours pill. Returns null when no hours configured (so the
 * pill doesn't render at all rather than misleading the customer).
 */
export function hoursPillText(
  hours: readonly ServiceHoursRow[],
  date: Date,
): { label: string; isOpen: boolean } | null {
  if (hours.length === 0) return null;
  const dow = date.getDay();
  const sameDow = hours.filter((h) => h.day_of_week === dow);
  if (sameDow.length === 0) {
    // Hours configured for other days, none for today → closed today.
    return { label: "Closed today", isOpen: false };
  }
  const closedAll = sameDow.every((h) => h.is_closed);
  if (closedAll) return { label: "Closed today", isOpen: false };
  const windows = windowsForDate(hours, date);
  if (windows.length === 0) return { label: "Closed today", isOpen: false };
  const parts = windows.map(
    (w) => `${formatClock(w.start)}–${formatClock(w.end)}`,
  );
  return { label: `Open today ${parts.join(" · ")}`, isOpen: true };
}

/**
 * The day's open windows as epoch-ms pairs — for feeding suggestNextFreeSlot
 * so alternative-time suggestions stay inside operating hours.
 */
export function openWindowsMsForDate(
  hours: readonly ServiceHoursRow[],
  date: Date,
): { start: number; end: number }[] {
  return windowsForDate(hours, date).map((w) => ({
    start: w.start.getTime(),
    end: w.end.getTime(),
  }));
}

/**
 * Whether [start, end) falls entirely within an open window on that date.
 * Fail-open when no hours are configured (matches the server trigger, which
 * only enforces when rows exist). Used for live, on-change feedback so the
 * customer isn't only told at submit time.
 */
export function isWithinServiceHours(
  hours: readonly ServiceHoursRow[],
  start: Date,
  end?: Date,
): boolean {
  if (hours.length === 0) return true;
  const windows = windowsForDate(hours, start);
  if (windows.length === 0) return false;
  const s = start.getTime();
  const e = (end ?? start).getTime();
  return windows.some((w) => s >= w.start.getTime() && e <= w.end.getTime());
}

// ─── helpers ──────────────────────────────────────────────────────────────

function parseTime(s: string): [number, number] {
  const [h, m] = s.split(":").map((p) => Number.parseInt(p, 10));
  return [h ?? 0, m ?? 0];
}

function formatClock(d: Date): string {
  return d.toLocaleTimeString("en-ZA", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
